import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../utils/api';
import Navbar from '../components/Navbar';
import {
  User,
  MapPin,
  Pencil,
  MusicNotes,
  GlobeHemisphereWest,
  Translate,
  ShieldCheck,
  CheckCircle,
  XCircle,
  Users,
  ArrowRight,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const profileTexts = {
  de: {
    communityProfile: 'WORLDofRADIO Community-Profil',
    admin: 'ADMIN',
    verified: 'VERIFIZIERT',
    favorites: 'Favoriten',
    comments: 'Kommentare',
    followers: 'Follower',
    following: 'Folgt',

    username: 'Benutzername',
    bio: 'Bio',
    favoriteMusicGenres: 'Lieblingsgenres',
    country: 'Land',
    writingLanguage: 'Schreibsprache',
    readingLanguage: 'Lesesprache',
    avatarUrl: 'Avatar-URL',

    usernameRuleLength: '3–24 Zeichen',
    usernameRuleLowercase: 'nur Kleinbuchstaben',
    usernameRuleNumbers: 'Zahlen erlaubt',
    usernameRuleUnderscore: 'Unterstrich erlaubt, keine Leerzeichen',
    usernameHint:
      'Beispiel: vms_worldofradio · keine Leerzeichen · keine Großbuchstaben',
    usernameInvalid:
      'Benutzername ungültig: 3–24 Zeichen, nur Kleinbuchstaben, Zahlen und Unterstrich. Keine Leerzeichen.',

    bioPlaceholder:
      'Erzähl der Radio-Community, was du liebst: Musik, Sender, Szenen, Länder...',
    genresPlaceholder: 'Techno, Drum and Bass, Reggae',
    genresHint: 'Trenne Genres mit Kommas.',
    countryPlaceholder: 'Deutschland',

    german: 'Deutsch',
    english: 'Englisch',
    norwegian: 'Norwegisch',
    swedish: 'Schwedisch',
    spanish: 'Spanisch',
    french: 'Französisch',
    italian: 'Italienisch',
    korean: 'Koreanisch',
    japanese: 'Japanisch',

    saving: 'Speichern...',
    saveProfile: 'Profil speichern',
    cancel: 'Abbrechen',

    publicIdentity: 'Öffentliche Identität',
    privateHint: 'Deine E-Mail und dein echter Name bleiben privat.',
    languageSettings: 'Spracheinstellungen',
    writes: 'Schreibt',
    reads: 'Liest',
    favoriteGenres: 'Lieblingsgenres',
    noGenres: 'Noch keine Genres hinzugefügt.',
    noCountry: 'Noch kein Land hinzugefügt.',
    noBio:
      'Noch keine Bio hinzugefügt. Erzähl der Community, welche Radiowelten du liebst.',
    editProfile: 'Profil bearbeiten',
    saveError:
      'Profil konnte nicht gespeichert werden. Bitte prüfe deine Eingaben.',

    friends: 'Freunde',
    friendsSubtitle: 'Deine bestätigten Kontakte auf WORLDofRADIO.',
    noFriends:
      'Noch keine Freunde vorhanden. Suche User und sende Freundschaftsanfragen.',
    loadingFriends: 'Freunde werden geladen...',
    viewProfile: 'Profil ansehen',
    noFriendCountry: 'Kein Land angegeben',
    friendGenres: 'Musikrichtungen',
    friendsSince: 'Verbunden seit',
  },

  en: {
    communityProfile: 'WORLDofRADIO Community Profile',
    admin: 'ADMIN',
    verified: 'VERIFIED',
    favorites: 'Favorites',
    comments: 'Comments',
    followers: 'Followers',
    following: 'Following',

    username: 'Username',
    bio: 'Bio',
    favoriteMusicGenres: 'Favorite Music Genres',
    country: 'Country',
    writingLanguage: 'Writing Language',
    readingLanguage: 'Reading Language',
    avatarUrl: 'Avatar URL',

    usernameRuleLength: '3–24 characters',
    usernameRuleLowercase: 'lowercase only',
    usernameRuleNumbers: 'numbers allowed',
    usernameRuleUnderscore: 'underscore allowed, no spaces',
    usernameHint:
      'Example: vms_worldofradio · no spaces · no uppercase letters',
    usernameInvalid:
      'Invalid username: 3–24 characters, only lowercase letters, numbers and underscore. No spaces.',

    bioPlaceholder:
      'Tell the radio community what you love: music, stations, scenes, countries...',
    genresPlaceholder: 'Techno, Drum and Bass, Reggae',
    genresHint: 'Separate genres with commas.',
    countryPlaceholder: 'Germany',

    german: 'German',
    english: 'English',
    norwegian: 'Norwegian',
    swedish: 'Swedish',
    spanish: 'Spanish',
    french: 'French',
    italian: 'Italian',
    korean: 'Korean',
    japanese: 'Japanese',

    saving: 'Saving...',
    saveProfile: 'Save Profile',
    cancel: 'Cancel',

    publicIdentity: 'Public Identity',
    privateHint: 'Your email and real name are private.',
    languageSettings: 'Language Settings',
    writes: 'Writes',
    reads: 'Reads',
    favoriteGenres: 'Favorite Genres',
    noGenres: 'No genres added yet.',
    noCountry: 'No country added yet.',
    noBio:
      'No bio added yet. Tell the community which radio worlds you love.',
    editProfile: 'Edit Profile',
    saveError:
      'Profile could not be saved. Please check your input.',

    friends: 'Friends',
    friendsSubtitle: 'Your confirmed contacts on WORLDofRADIO.',
    noFriends: 'No friends yet. Search users and send friend requests.',
    loadingFriends: 'Loading friends...',
    viewProfile: 'View profile',
    noFriendCountry: 'No country provided',
    friendGenres: 'Music genres',
    friendsSince: 'Connected since',
  },

  no: {
    communityProfile: 'WORLDofRADIO fellesskapsprofil',
    admin: 'ADMIN',
    verified: 'VERIFISERT',
    favorites: 'Favoritter',
    comments: 'Kommentarer',
    followers: 'Følgere',
    following: 'Følger',

    username: 'Brukernavn',
    bio: 'Bio',
    favoriteMusicGenres: 'Favorittsjangre',
    country: 'Land',
    writingLanguage: 'Skrivespråk',
    readingLanguage: 'Lesespråk',
    avatarUrl: 'Avatar-URL',

    usernameRuleLength: '3–24 tegn',
    usernameRuleLowercase: 'kun små bokstaver',
    usernameRuleNumbers: 'tall tillatt',
    usernameRuleUnderscore: 'understrek tillatt, ingen mellomrom',
    usernameHint:
      'Eksempel: vms_worldofradio · ingen mellomrom · ingen store bokstaver',
    usernameInvalid:
      'Ugyldig brukernavn: 3–24 tegn, kun små bokstaver, tall og understrek. Ingen mellomrom.',

    bioPlaceholder:
      'Fortell radiofellesskapet hva du elsker: musikk, stasjoner, scener, land...',
    genresPlaceholder: 'Techno, Drum and Bass, Reggae',
    genresHint: 'Skill sjangre med komma.',
    countryPlaceholder: 'Tyskland',

    german: 'Tysk',
    english: 'Engelsk',
    norwegian: 'Norsk',
    swedish: 'Svensk',
    spanish: 'Spansk',
    french: 'Fransk',
    italian: 'Italiensk',
    korean: 'Koreansk',
    japanese: 'Japansk',

    saving: 'Lagrer...',
    saveProfile: 'Lagre profil',
    cancel: 'Avbryt',

    publicIdentity: 'Offentlig identitet',
    privateHint: 'E-postadressen og det ekte navnet ditt er private.',
    languageSettings: 'Språkinnstillinger',
    writes: 'Skriver',
    reads: 'Leser',
    favoriteGenres: 'Favorittsjangre',
    noGenres: 'Ingen sjangre lagt til ennå.',
    noCountry: 'Ingen land lagt til ennå.',
    noBio:
      'Ingen bio lagt til ennå. Fortell fellesskapet hvilke radioverdener du elsker.',
    editProfile: 'Rediger profil',
    saveError:
      'Profilen kunne ikke lagres. Vennligst sjekk opplysningene dine.',

    friends: 'Venner',
    friendsSubtitle: 'Dine bekreftede kontakter på WORLDofRADIO.',
    noFriends: 'Ingen venner ennå. Søk etter brukere og send venneforespørsler.',
    loadingFriends: 'Laster venner...',
    viewProfile: 'Vis profil',
    noFriendCountry: 'Ingen land angitt',
    friendGenres: 'Musikksjangre',
    friendsSince: 'Tilkoblet siden',
  },

  ko: {
    communityProfile: 'WORLDofRADIO 커뮤니티 프로필',
    admin: '관리자',
    verified: '인증됨',
    favorites: '즐겨찾기',
    comments: '댓글',
    followers: '팔로워',
    following: '팔로잉',

    username: '사용자 이름',
    bio: '소개',
    favoriteMusicGenres: '좋아하는 음악 장르',
    country: '국가',
    writingLanguage: '작성 언어',
    readingLanguage: '읽기 언어',
    avatarUrl: '아바타 URL',

    usernameRuleLength: '3–24자',
    usernameRuleLowercase: '소문자만 사용',
    usernameRuleNumbers: '숫자 사용 가능',
    usernameRuleUnderscore: '밑줄 사용 가능, 공백 없음',
    usernameHint: '예: vms_worldofradio · 공백 없음 · 대문자 없음',
    usernameInvalid:
      '사용자 이름이 올바르지 않습니다: 3–24자, 소문자, 숫자, 밑줄만 사용할 수 있습니다. 공백은 사용할 수 없습니다.',

    bioPlaceholder:
      '라디오 커뮤니티에 당신이 좋아하는 음악, 방송국, 씬, 국가를 알려주세요...',
    genresPlaceholder: 'Techno, Drum and Bass, Reggae',
    genresHint: '장르는 쉼표로 구분하세요.',
    countryPlaceholder: 'Germany',

    german: '독일어',
    english: '영어',
    norwegian: '노르웨이어',
    swedish: '스웨덴어',
    spanish: '스페인어',
    french: '프랑스어',
    italian: '이탈리아어',
    korean: '한국어',
    japanese: '일본어',

    saving: '저장 중...',
    saveProfile: '프로필 저장',
    cancel: '취소',

    publicIdentity: '공개 신원',
    privateHint: '이메일과 실명은 비공개입니다.',
    languageSettings: '언어 설정',
    writes: '작성',
    reads: '읽기',
    favoriteGenres: '좋아하는 장르',
    noGenres: '아직 추가된 장르가 없습니다.',
    noCountry: '아직 추가된 국가가 없습니다.',
    noBio:
      '아직 소개가 없습니다. 어떤 라디오 세계를 좋아하는지 커뮤니티에 알려주세요.',
    editProfile: '프로필 편집',
    saveError:
      '프로필을 저장할 수 없습니다. 입력 내용을 확인해 주세요.',

    friends: '친구',
    friendsSubtitle: 'WORLDofRADIO에서 확인된 친구 목록입니다.',
    noFriends: '아직 친구가 없습니다. 사용자를 검색하고 친구 요청을 보내세요.',
    loadingFriends: '친구를 불러오는 중...',
    viewProfile: '프로필 보기',
    noFriendCountry: '국가 정보 없음',
    friendGenres: '음악 장르',
    friendsSince: '연결된 날짜',
  },

  fi: {
    communityProfile: 'WORLDofRADIO-yhteisöprofiili',
    admin: 'YLLÄPITO',
    verified: 'VAHVISTETTU',
    favorites: 'Suosikit',
    comments: 'Kommentit',
    followers: 'Seuraajat',
    following: 'Seuratut',

    username: 'Käyttäjänimi',
    bio: 'Bio',
    favoriteMusicGenres: 'Suosikkigenret',
    country: 'Maa',
    writingLanguage: 'Kirjoituskieli',
    readingLanguage: 'Lukukieli',
    avatarUrl: 'Avatar-URL',

    usernameRuleLength: '3–24 merkkiä',
    usernameRuleLowercase: 'vain pienet kirjaimet',
    usernameRuleNumbers: 'numerot sallittu',
    usernameRuleUnderscore: 'alaviiva sallittu, ei välilyöntejä',
    usernameHint:
      'Esimerkki: vms_worldofradio · ei välilyöntejä · ei isoja kirjaimia',
    usernameInvalid:
      'Virheellinen käyttäjänimi: 3–24 merkkiä, vain pieniä kirjaimia, numeroita ja alaviiva. Ei välilyöntejä.',

    bioPlaceholder:
      'Kerro radioyhteisölle, mitä rakastat: musiikkia, asemia, skenejä, maita...',
    genresPlaceholder: 'Techno, Drum and Bass, Reggae',
    genresHint: 'Erottele genret pilkuilla.',
    countryPlaceholder: 'Saksa',

    german: 'Saksa',
    english: 'Englanti',
    norwegian: 'Norja',
    swedish: 'Ruotsi',
    spanish: 'Espanja',
    french: 'Ranska',
    italian: 'Italia',
    korean: 'Korea',
    japanese: 'Japani',

    saving: 'Tallennetaan...',
    saveProfile: 'Tallenna profiili',
    cancel: 'Peruuta',

    publicIdentity: 'Julkinen identiteetti',
    privateHint: 'Sähköpostisi ja oikea nimesi ovat yksityisiä.',
    languageSettings: 'Kieliasetukset',
    writes: 'Kirjoittaa',
    reads: 'Lukee',
    favoriteGenres: 'Suosikkigenret',
    noGenres: 'Genrejä ei ole vielä lisätty.',
    noCountry: 'Maata ei ole vielä lisätty.',
    noBio:
      'Bioa ei ole vielä lisätty. Kerro yhteisölle, millaisista radiomaailmoista pidät.',
    editProfile: 'Muokkaa profiilia',
    saveError:
      'Profiilia ei voitu tallentaa. Tarkista syötteesi.',

    friends: 'Ystävät',
    friendsSubtitle: 'Vahvistetut kontaktisi WORLDofRADIOssa.',
    noFriends: 'Ei ystäviä vielä. Etsi käyttäjiä ja lähetä ystäväpyyntöjä.',
    loadingFriends: 'Ladataan ystäviä...',
    viewProfile: 'Näytä profiili',
    noFriendCountry: 'Maata ei annettu',
    friendGenres: 'Musiikkityylit',
    friendsSince: 'Yhdistetty alkaen',
  },
};

const languageOptions = (text) => [
  { value: 'de', label: text.german },
  { value: 'en', label: text.english },
  { value: 'no', label: text.norwegian },
  { value: 'sv', label: text.swedish },
  { value: 'es', label: text.spanish },
  { value: 'fr', label: text.french },
  { value: 'it', label: text.italian },
  { value: 'ko', label: text.korean },
  { value: 'ja', label: text.japanese },
];

const Profile = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const { language } = useLanguage();
  const text = profileTexts[language] || profileTexts.en;

  const [editing, setEditing] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    bio: '',
    country: '',
    avatar: '',
    music_genres: '',
    preferred_language: 'de',
    original_language: 'de',
  });

  const [stats, setStats] = useState({
    favorites: 0,
    comments: 0,
    followers: 0,
    following: 0,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        bio: user.bio || '',
        country: user.country || '',
        avatar: user.avatar || user.avatar_url || '',
        music_genres: Array.isArray(user.music_genres)
          ? user.music_genres.join(', ')
          : user.music_genres || '',
        preferred_language: user.preferred_language || 'de',
        original_language: user.original_language || 'de',
      });

      loadStats();
      loadFriends();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadStats = async () => {
    if (!user?.username) return;

    try {
      const res = await api.getUserProfile(user.username);
      setStats(
        res.data.stats || {
          favorites: 0,
          comments: 0,
          followers: 0,
          following: 0,
        }
      );
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadFriends = async () => {
    try {
      setFriendsLoading(true);
      const res = await api.getFriends();
      setFriends(res.data || []);
    } catch (error) {
      console.error('Failed to load friends:', error);
      setFriends([]);
    } finally {
      setFriendsLoading(false);
    }
  };

  const usernameValue = formData.username.trim();

  const usernameRules = [
    {
      label: text.usernameRuleLength,
      valid: usernameValue.length >= 3 && usernameValue.length <= 24,
    },
    {
      label: text.usernameRuleLowercase,
      valid: !/[A-Z]/.test(formData.username),
    },
    {
      label: text.usernameRuleNumbers,
      valid: /^[a-z0-9_]*$/.test(formData.username),
    },
    {
      label: text.usernameRuleUnderscore,
      valid:
        !/\s/.test(formData.username) && /^[a-z0-9_]*$/.test(formData.username),
    },
  ];

  const isUsernameValid = /^[a-z0-9_]{3,24}$/.test(usernameValue);

  const UsernameRule = ({ valid, label }) => {
    return (
      <div
        className={`flex items-center gap-2 text-xs ${valid ? 'text-cyan-300' : 'text-gray-500'
          }`}
      >
        {valid ? (
          <CheckCircle size={15} weight="fill" className="text-cyan-400" />
        ) : (
          <XCircle size={15} className="text-gray-600" />
        )}
        <span>{label}</span>
      </div>
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((current) => ({
      ...current,
      [name]: name === 'username' ? value.toLowerCase() : value,
    }));

    if (profileError) {
      setProfileError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanUsername = formData.username.trim().toLowerCase();

    if (!/^[a-z0-9_]{3,24}$/.test(cleanUsername)) {
      setProfileError(text.usernameInvalid);
      return;
    }

    const payload = {
      username: cleanUsername,
      bio: formData.bio.trim(),
      country: formData.country.trim(),
      avatar: formData.avatar.trim(),
      music_genres: formData.music_genres
        .split(',')
        .map((genre) => genre.trim())
        .filter(Boolean),
      preferred_language: formData.preferred_language,
      original_language: formData.original_language,
    };

    setProfileError('');
    setSavingProfile(true);

    try {
      await api.updateProfile(payload);
      await checkAuth();
      setEditing(false);
      await loadFriends();
    } catch (error) {
      console.error('Failed to update profile:', error);

      const backendMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        text.saveError;

      setProfileError(backendMessage);
    } finally {
      setSavingProfile(false);
    }
  };

  const formatFriendDate = (value) => {
    if (!value) return null;

    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return null;

      return date.toLocaleDateString(
        language === 'de'
          ? 'de-DE'
          : language === 'ko'
            ? 'ko-KR'
            : language === 'no'
              ? 'nb-NO'
              : language === 'fi'
                ? 'fi-FI'
                : 'en-US'
      );
    } catch {
      return null;
    }
  };

  const getFriendGenres = (friend) => {
    if (Array.isArray(friend.music_genres)) {
      return friend.music_genres.filter(Boolean);
    }

    if (friend.music_genres) {
      return [friend.music_genres];
    }

    return [];
  };

  const openFriendProfile = (friend) => {
    if (!friend?.username) return;
    navigate(`/user/${friend.username}`);
  };

  if (!user) {
    return null;
  }

  const publicUsername = user.username || 'user';
  const avatarSource = user.avatar || user.avatar_url || '';
  const fallbackLetter = publicUsername.charAt(0).toUpperCase();

  const musicGenres = Array.isArray(user.music_genres)
    ? user.music_genres
    : user.music_genres
      ? [user.music_genres]
      : [];

  const preferredLanguage = user.preferred_language || 'de';
  const originalLanguage = user.original_language || 'de';
  const options = languageOptions(text);

  return (
    <div className="min-h-screen bg-[#050505] pb-24 md:pb-0">
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <div className="backdrop-filter backdrop-blur-2xl bg-black/60 border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)] rounded-3xl p-8 lg:p-10">
          <div className="flex flex-col items-center mb-10">
            <Avatar className="w-32 h-32 border-4 border-cyan-500/50 mb-5">
              <AvatarImage src={avatarSource} alt={publicUsername} />
              <AvatarFallback className="bg-cyan-500/20 text-cyan-400 font-bold text-4xl">
                {fallbackLetter}
              </AvatarFallback>
            </Avatar>

            <h1
              className="text-3xl tracking-tighter font-black text-white mb-1"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              @{publicUsername}
            </h1>

            <p className="text-gray-400 text-center">{text.communityProfile}</p>

            <div className="flex items-center gap-2 mt-3">
              {user.role === 'admin' && (
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded-full">
                  {text.admin}
                </span>
              )}

              {user.is_verified && (
                <span className="px-3 py-1 bg-green-500/15 text-green-400 text-xs font-bold rounded-full flex items-center gap-1">
                  <ShieldCheck size={14} />
                  {text.verified}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4 mb-10">
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">
                {stats.favorites}
              </div>
              <div className="text-sm text-gray-400">{text.favorites}</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">
                {stats.comments}
              </div>
              <div className="text-sm text-gray-400">{text.comments}</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">
                {friends.length}
              </div>
              <div className="text-sm text-gray-400">{text.friends}</div>
            </div>

            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-400">
                {stats.following}
              </div>
              <div className="text-sm text-gray-400">{text.following}</div>
            </div>
          </div>

          {editing ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              {profileError && (
                <div
                  className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 text-sm text-red-400"
                  role="alert"
                >
                  {profileError}
                </div>
              )}

              <div>
                <Label htmlFor="username" className="text-white mb-2 block">
                  {text.username}
                </Label>

                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`bg-[#0A0E17] text-white rounded-xl ${formData.username && !isUsernameValid
                      ? 'border-red-500/60 focus:border-red-500'
                      : 'border-white/10 focus:border-cyan-500'
                    }`}
                  placeholder="vms_worldofradio"
                />

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  {usernameRules.map((rule) => (
                    <UsernameRule
                      key={rule.label}
                      valid={rule.valid}
                      label={rule.label}
                    />
                  ))}
                </div>

                <p className="text-xs text-gray-500 mt-2">
                  {text.usernameHint}
                </p>
              </div>

              <div>
                <Label htmlFor="bio" className="text-white mb-2 block">
                  {text.bio}
                </Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  className="bg-[#0A0E17] border-white/10 text-white rounded-xl min-h-24 resize-none"
                  placeholder={text.bioPlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="music_genres" className="text-white mb-2 block">
                  {text.favoriteMusicGenres}
                </Label>
                <Input
                  id="music_genres"
                  name="music_genres"
                  value={formData.music_genres}
                  onChange={handleChange}
                  className="bg-[#0A0E17] border-white/10 text-white rounded-xl"
                  placeholder={text.genresPlaceholder}
                />
                <p className="text-xs text-gray-500 mt-2">{text.genresHint}</p>
              </div>

              <div>
                <Label htmlFor="country" className="text-white mb-2 block">
                  {text.country}
                </Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="bg-[#0A0E17] border-white/10 text-white rounded-xl"
                  placeholder={text.countryPlaceholder}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor="original_language"
                    className="text-white mb-2 block"
                  >
                    {text.writingLanguage}
                  </Label>
                  <select
                    id="original_language"
                    name="original_language"
                    value={formData.original_language}
                    onChange={handleChange}
                    className="w-full bg-[#0A0E17] border border-white/10 text-white rounded-xl px-4 py-3 outline-none"
                  >
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label
                    htmlFor="preferred_language"
                    className="text-white mb-2 block"
                  >
                    {text.readingLanguage}
                  </Label>
                  <select
                    id="preferred_language"
                    name="preferred_language"
                    value={formData.preferred_language}
                    onChange={handleChange}
                    className="w-full bg-[#0A0E17] border border-white/10 text-white rounded-xl px-4 py-3 outline-none"
                  >
                    {options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="avatar" className="text-white mb-2 block">
                  {text.avatarUrl}
                </Label>
                <Input
                  id="avatar"
                  name="avatar"
                  value={formData.avatar}
                  onChange={handleChange}
                  className="bg-[#0A0E17] border-white/10 text-white rounded-xl"
                  placeholder="https://..."
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={savingProfile || !isUsernameValid}
                  className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingProfile ? text.saving : text.saveProfile}
                </Button>

                <Button
                  type="button"
                  onClick={() => {
                    setProfileError('');
                    setEditing(false);
                  }}
                  variant="outline"
                  className="rounded-full border-white/10 bg-white/5 text-white hover:bg-white/10"
                >
                  {text.cancel}
                </Button>
              </div>
            </form>
          ) : (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                  <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                    <User size={20} />
                    {text.publicIdentity}
                  </div>
                  <p className="text-gray-300">@{publicUsername}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    {text.privateHint}
                  </p>
                </div>

                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                  <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                    <Translate size={20} />
                    {text.languageSettings}
                  </div>
                  <p className="text-gray-300">
                    {text.writes}: {originalLanguage.toUpperCase()}
                  </p>
                  <p className="text-gray-300">
                    {text.reads}: {preferredLanguage.toUpperCase()}
                  </p>
                </div>

                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                  <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                    <MusicNotes size={20} />
                    {text.favoriteGenres}
                  </div>

                  {musicGenres.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {musicGenres.map((genre) => (
                        <span
                          key={genre}
                          className="px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-300 text-sm border border-cyan-500/20"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500">{text.noGenres}</p>
                  )}
                </div>

                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                  <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                    <GlobeHemisphereWest size={20} />
                    {text.country}
                  </div>

                  {user.country ? (
                    <div className="flex items-center gap-2 text-gray-300">
                      <MapPin size={18} className="text-cyan-400" />
                      <span>{user.country}</span>
                    </div>
                  ) : (
                    <p className="text-gray-500">{text.noCountry}</p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 mb-8">
                <div className="flex items-center gap-2 text-cyan-400 font-bold mb-3">
                  <MusicNotes size={20} />
                  {text.bio}
                </div>

                {user.bio ? (
                  <p className="text-gray-300 leading-relaxed">{user.bio}</p>
                ) : (
                  <p className="text-gray-500">{text.noBio}</p>
                )}
              </div>

              <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5 mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
                  <div>
                    <div className="flex items-center gap-2 text-cyan-400 font-bold mb-1">
                      <Users size={22} />
                      {text.friends}
                    </div>
                    <p className="text-sm text-gray-500">
                      {text.friendsSubtitle}
                    </p>
                  </div>

                  <span className="inline-flex items-center justify-center rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-sm font-bold text-cyan-300">
                    {friends.length}
                  </span>
                </div>

                {friendsLoading ? (
                  <p className="text-gray-500">{text.loadingFriends}</p>
                ) : friends.length === 0 ? (
                  <p className="text-gray-500">{text.noFriends}</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {friends.map((friend) => {
                      const friendUsername = friend.username || 'user';
                      const friendAvatar = friend.avatar || friend.avatar_url || '';
                      const friendLetter = friendUsername.charAt(0).toUpperCase();
                      const friendGenres = getFriendGenres(friend);
                      const friendDate = formatFriendDate(friend.friends_since);

                      return (
                        <button
                          key={friend.user_id || friend.friendship_id || friendUsername}
                          type="button"
                          onClick={() => openFriendProfile(friend)}
                          className="group text-left rounded-2xl border border-white/10 bg-[#0A0E17] p-4 hover:border-cyan-400/40 hover:bg-white/[0.04] transition-all"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-4 min-w-0">
                              <Avatar className="w-12 h-12 border border-cyan-500/30">
                                <AvatarImage src={friendAvatar} alt={friendUsername} />
                                <AvatarFallback className="bg-cyan-500/20 text-cyan-400 font-bold">
                                  {friendLetter}
                                </AvatarFallback>
                              </Avatar>

                              <div className="min-w-0">
                                <p className="font-bold text-white truncate">
                                  @{friendUsername}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {friend.country || text.noFriendCountry}
                                </p>
                              </div>
                            </div>

                            <ArrowRight
                              size={20}
                              className="text-gray-600 group-hover:text-cyan-400 transition-colors flex-shrink-0"
                            />
                          </div>

                          {friendGenres.length > 0 && (
                            <p className="text-xs text-gray-500 mt-4 line-clamp-1">
                              {text.friendGenres}: {friendGenres.join(', ')}
                            </p>
                          )}

                          {friendDate && (
                            <p className="text-xs text-gray-600 mt-2">
                              {text.friendsSince}: {friendDate}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <Button
                data-testid="edit-profile-btn"
                onClick={() => {
                  setProfileError('');
                  setEditing(true);
                }}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full flex items-center gap-2"
              >
                <Pencil size={18} />
                {text.editProfile}
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Profile;